// ============================================
// OrderDetail — Detalle de un pedido (/account/orders/:orderId)
// Compartido por clientes y socios. Muestra items, totales, dirección,
// rastreo de envío y facturación CFDI por pedido (emitir/descargar).
// ============================================

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCustomer } from '../context/CustomerContext';
import { useCart } from '../context/CartContext';
import {
  getOrderById,
  getOrderInvoice,
  downloadInvoiceFile,
} from '../services/medusa/distributor';
import { getTracking } from '../services/medusa/tracking';
import {
  formatPrice,
  formatDate,
  getFulfillmentStatusLabel,
  getPaymentStatusLabel,
  ORDER_STEPS,
  stepIndexFromOrder,
} from '../services/orderFormat';
import InvoiceModal from '../components/account/InvoiceModal';
import LoginModal from '../components/account/LoginModal';
import SEO from '../components/SEO';
import '../styles/OrderDetail.css';

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useCustomer() || {};
  const { reorder } = useCart() || {};

  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getOrderById(token, orderId);
      if (!data) {
        setError('No encontramos este pedido.');
        return;
      }
      setOrder(data);
      // La factura es best-effort: si no existe, getOrderInvoice devuelve null.
      const inv = await getOrderInvoice(token, orderId);
      setInvoice(inv);
    } catch (err) {
      console.error('Error cargando pedido:', err);
      setError('No se pudo cargar el pedido.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReorder = async () => {
    if (!order?.items?.length || reordering) return;
    setReordering(true);
    try {
      const added = await reorder(order.items);
      if (added > 0) {
        navigate('/cart');
      } else {
        alert('No hay productos re-ordenables en este pedido.');
      }
    } catch (err) {
      alert('No se pudo reordenar. Intenta de nuevo.');
    } finally {
      setReordering(false);
    }
  };

  const downloadInvoice = (kind) => {
    const suffix = kind === 'xml' ? 'xml' : 'pdf';
    downloadInvoiceFile(
      token,
      order.id,
      kind,
      `factura-${order.display_id || order.id}.${suffix}`
    ).catch((err) => alert(err.message));
  };

  // ─── Estados de UI ────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <>
        <div className="order-detail order-detail--gate">
          <h1>Detalle de pedido</h1>
          <p>Inicia sesión para ver tu pedido.</p>
          <button className="od-btn" onClick={() => setShowLogin(true)}>
            Iniciar sesión
          </button>
        </div>
        <LoginModal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          onLoginSuccess={() => setShowLogin(false)}
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="order-detail order-detail--state">
        <p>Cargando pedido…</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail order-detail--state">
        <p>{error || 'Pedido no disponible.'}</p>
        <Link to="/socio" className="od-link">
          ← Volver al panel
        </Link>
      </div>
    );
  }

  const tracking = getTracking(order);
  const stepIdx = stepIndexFromOrder(order);
  const cc = order.currency_code;
  const address = order.shipping_address;
  const rutinaHandles = [...new Set((order.items || []).map((i) => i.product_handle).filter(Boolean))];

  return (
    <div className="order-detail">
      <SEO title={`Pedido #${order.display_id || ''}`} noindex />

      <div className="od-topbar">
        <button className="od-back" onClick={() => navigate(-1)}>
          ← Volver
        </button>
        <div className="od-topbar__actions">
          {rutinaHandles.length > 0 && (
            <Link
              to={`/mi-rutina?productos=${encodeURIComponent(rutinaHandles.join(','))}`}
              className="od-btn od-btn--ghost"
            >
              Ver mi rutina
            </Link>
          )}
          <button
            className="od-btn od-btn--reorder"
            onClick={handleReorder}
            disabled={reordering}
          >
            {reordering ? 'Agregando…' : 'Reordenar'}
          </button>
        </div>
      </div>

      {/* Header */}
      <header className="od-header">
        <div>
          <h1 className="od-title">
            Pedido #{order.display_id || order.id?.slice(-8)}
          </h1>
          <p className="od-date">{formatDate(order.created_at)}</p>
        </div>
        <div className="od-badges">
          <span className="od-badge">
            {getFulfillmentStatusLabel(order.fulfillment_status)}
          </span>
          <span className="od-badge od-badge--pay">
            {getPaymentStatusLabel(order.payment_status)}
          </span>
        </div>
      </header>

      {/* Stepper */}
      <ol className="od-stepper">
        {ORDER_STEPS.map((label, idx) => (
          <li
            key={label}
            className={`od-stepper__step ${
              idx < stepIdx
                ? 'od-stepper__step--done'
                : idx === stepIdx
                ? 'od-stepper__step--active'
                : ''
            }`}
          >
            <span className="od-stepper__dot" />
            <span className="od-stepper__label">{label}</span>
          </li>
        ))}
      </ol>

      <div className="od-grid">
        {/* Items */}
        <section className="od-card od-items">
          <h2 className="od-card__title">Productos</h2>
          {(order.items || []).map((item) => {
            const isGift = item?.metadata?.distributor_gift;
            return (
              <div key={item.id} className="od-item">
                {item.thumbnail && (
                  <img
                    src={item.thumbnail}
                    alt={item.title || item.product_title}
                    className="od-item__img"
                  />
                )}
                <div className="od-item__info">
                  <span className="od-item__name">
                    {item.product_title || item.title}
                    {isGift && <span className="od-gift">🎁 Regalo</span>}
                  </span>
                  {item.variant_title && item.variant_title !== 'Default' && (
                    <span className="od-item__variant">{item.variant_title}</span>
                  )}
                  <span className="od-item__qty">Cantidad: {item.quantity}</span>
                </div>
                <span className="od-item__price">
                  {isGift
                    ? 'Gratis'
                    : formatPrice(item.unit_price * item.quantity, cc)}
                </span>
              </div>
            );
          })}
        </section>

        {/* Resumen lateral */}
        <aside className="od-side">
          {/* Totales */}
          <section className="od-card">
            <h2 className="od-card__title">Resumen</h2>
            <div className="od-total-row">
              <span>Subtotal</span>
              <span>{formatPrice(order.subtotal ?? order.item_total, cc)}</span>
            </div>
            {order.shipping_total > 0 && (
              <div className="od-total-row">
                <span>Envío</span>
                <span>{formatPrice(order.shipping_total, cc)}</span>
              </div>
            )}
            {order.discount_total > 0 && (
              <div className="od-total-row od-total-row--discount">
                <span>Descuento</span>
                <span>-{formatPrice(order.discount_total, cc)}</span>
              </div>
            )}
            <div className="od-total-row od-total-row--final">
              <span>Total</span>
              <span>{formatPrice(order.total, cc)}</span>
            </div>
          </section>

          {/* Envío / dirección */}
          {address && (
            <section className="od-card">
              <h2 className="od-card__title">Envío</h2>
              <p className="od-address">
                {[address.first_name, address.last_name]
                  .filter(Boolean)
                  .join(' ')}
                <br />
                {address.address_1}
                {address.address_2 ? `, ${address.address_2}` : ''}
                <br />
                {[address.city, address.province, address.postal_code]
                  .filter(Boolean)
                  .join(', ')}
              </p>
              <div className="od-tracking">
                <span className="od-tracking__label">
                  Guía de envío{tracking?.carrier ? ` · ${tracking.carrier}` : ''}:
                </span>{' '}
                {tracking?.url ? (
                  <a
                    href={tracking.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="od-link"
                  >
                    {tracking.number || 'Rastrear'}
                  </a>
                ) : (
                  <strong>{tracking?.number || 'Pendiente'}</strong>
                )}
              </div>
            </section>
          )}

          {/* Factura */}
          <section className="od-card">
            <h2 className="od-card__title">Factura</h2>
            {invoice?.status === 'requested' ? (
              // Facturación manual: la solicitud ya llegó a contabilidad.
              <p className="od-invoice__hint">
                Recibimos tu solicitud
                {invoice.requested_at
                  ? ` el ${new Date(invoice.requested_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}`
                  : ''}
                . Te enviaremos la factura por correo en un máximo de 3 días hábiles.
              </p>
            ) : invoice ? (
              <div className="od-invoice">
                <p className="od-invoice__id">
                  Factura {invoice.series ? `${invoice.series}-` : ''}
                  {invoice.folio_number || invoice.id?.slice(-6)}
                </p>
                <div className="od-invoice__btns">
                  <button className="od-btn" onClick={() => downloadInvoice('pdf')}>
                    PDF
                  </button>
                  <button className="od-btn" onClick={() => downloadInvoice('xml')}>
                    XML
                  </button>
                  {invoice.verification_url && (
                    <a
                      href={invoice.verification_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="od-btn od-btn--ghost"
                    >
                      Verificar en SAT
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <>
                <p className="od-invoice__hint">
                  ¿Necesitas factura de este pedido?
                </p>
                <button
                  className="od-btn od-btn--cta"
                  onClick={() => setInvoiceModalOpen(true)}
                >
                  Solicitar factura
                </button>
              </>
            )}
          </section>
        </aside>
      </div>

      <InvoiceModal
        open={invoiceModalOpen}
        orderId={order.id}
        token={token}
        onClose={() => setInvoiceModalOpen(false)}
        onSuccess={(inv) => {
          setInvoice(inv);
          setInvoiceModalOpen(false);
        }}
      />
    </div>
  );
}
